import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coursesApi } from 'shared/services/coursesApi';
import { queryKeys } from '../queryConfig';

/**
 * Hook to search for a TalentLMS user by email (and fallback by ID)
 */
export const useTalentLMSUser = (email?: string, talentLMSUserId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.courses.all, 'talentLMSUser', email, talentLMSUserId],
    queryFn: async () => {
      // First try searching by email
      if (email) {
        const response = await coursesApi.searchTalentLMSUser({ email });
        if (response.success && response.data) {
          const userData = Array.isArray(response.data) ? response.data[0] : response.data;
          if (userData) return userData;
        }
      }

      // Fallback: search by talentLMSUserId
      if (talentLMSUserId) {
        const idResponse = await coursesApi.searchTalentLMSUser({ id: talentLMSUserId });
        if (idResponse.success && idResponse.data) {
          const userData = Array.isArray(idResponse.data) ? idResponse.data[0] : idResponse.data;
          if (userData) return userData;
        }
      }

      return null;
    },
    enabled: !!email || !!talentLMSUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Hook to fetch user courses (initial + additional + enrollments)
 */
export const useUserCourses = (userId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.courses.all, 'userCourses', userId],
    queryFn: async () => {
      try {
        const response = await coursesApi.getUserCourses(userId!);
        if (response.success && response.data) {
          return response.data;
        }
      } catch {
        // Fall through to default
      }
      return {
        initial: [],
        additional: [],
        enrollments: [],
        stats: null,
      };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Hook to fetch backend (TalentLMS) courses for a user
 */
export const useBackendCourses = (userId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.courses.all, 'backendCourses', userId],
    queryFn: async () => {
      try {
        const data = await coursesApi.getUserTalentLMSCourses(userId!);
        return data.courses || [];
      } catch {
        return [];
      }
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Hook to fetch available TalentLMS courses (active only)
 */
export const useAvailableCourses = () => {
  return useQuery({
    queryKey: [...queryKeys.courses.all, 'available'],
    queryFn: async () => {
      try {
        const response = await coursesApi.getTalentLMSAvailableCourses();
        if (response.success && response.data) {
          return response.data.filter(
            (course: any) =>
              course.status === 'active' || course.status === 'Active' || !course.status
          );
        }
      } catch {
        // Fall through to default
      }
      return [] as any[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Mutation hook to enroll in a TalentLMS course
 */
export const useEnrollInCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { userId: string; courseId: string; role: 'learner' | 'instructor' }) =>
      coursesApi.enrollToCourse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
    },
  });
};

/**
 * Mutation hook to unenroll from a TalentLMS course
 */
export const useUnenrollFromCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { userId: string; courseId: string }) =>
      coursesApi.unenrollFromCourse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
    },
  });
};
